import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Brokers from "@/pages/Brokers";
import Settings from "@/pages/Settings";
import Signals from "@/pages/Signals";
import Trades from "@/pages/Trades";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import VerifyEmail from "@/pages/VerifyEmail";
import Paywall from "@/pages/Paywall";

function Loading() {
  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-500 flex items-center justify-center font-mono text-sm">
      Initialising…
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (!user.has_access) return <Navigate to="/paywall" replace />;
  return <Layout>{children}</Layout>;
}

function PaywallGate({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.has_access) return <Navigate to="/app" replace />;
  return children;
}

function AdminProtected({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (user && user.role === "admin") return <Navigate to="/admin" replace />;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster
          theme="dark"
          position="bottom-right"
          toastOptions={{
            style: {
              background: "#121214",
              border: "1px solid rgba(255,255,255,0.1)",
              color: "#FAFAFA",
              fontFamily: "JetBrains Mono, monospace",
              fontSize: "12px",
              borderRadius: "2px",
            },
          }}
        />
        <Routes>
          {/* Public marketing/landing */}
          <Route path="/" element={<PublicOnly><Landing /></PublicOnly>} />
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
          <Route path="/verify-email" element={<VerifyEmail />} />

          {/* Admin */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminProtected><AdminDashboard /></AdminProtected>} />

          {/* Paywall — between verified login and app access */}
          <Route path="/paywall" element={<PaywallGate><Paywall /></PaywallGate>} />

          {/* Authenticated trader app */}
          <Route path="/app" element={<Protected><Dashboard /></Protected>} />
          <Route path="/brokers" element={<Protected><Brokers /></Protected>} />
          <Route path="/settings" element={<Protected><Settings /></Protected>} />
          <Route path="/signals" element={<Protected><Signals /></Protected>} />
          <Route path="/trades" element={<Protected><Trades /></Protected>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
